/**
 * Skeleton loading screen for the profile page.
 *
 * `ProfileBodySkeleton` stands in for the profile's own content column — the
 * photo-and-name heading and the discussion-history section, plus the owner's
 * edit controls. `ProfileScreen` renders it alone while the profile read is in
 * flight, since the surrounding chrome and the signed-in user are known by
 * then. The cold-load screen renders before the user is known, so it shows the
 * layout a visitor sees.
 * `SkeletonStandaloneProfileScreen` and `SkeletonSidebarProfileScreen` wrap
 * that body in the chrome a profile page loads into: the top-level nav bar and
 * `StandaloneLayout` for a standalone page, or the host panel's nav bar above a
 * `ConversationScreen` in a sidebar.
 */

import { createThemedStyles } from '@repo/theme/styles';
import { Pad, PadBox, Separator } from 'component/basics';
import { AvatarHeadingSkeleton } from 'component/progress/avatar-heading-skeleton';
import { BaseSkeleton } from 'component/progress/base-skeleton';
import { ButtonSkeleton } from 'component/progress/button-skeleton';
import { LoadingAnnouncement } from 'component/progress/loading-announcement';
import { NavBarSkeleton, SidebarNavBarSkeleton } from 'component/progress/nav-bar-skeleton';
import { TextContainerSkeleton } from 'component/progress/text-container-skeleton';
import { ConversationScreen } from 'component/screen';
import { View } from 'react-native';
import { StandaloneLayout } from 'structure/question/standalone-layout';
import { useIsDesktopBreakpoint } from 'util/layout';

const ANSWER_ROW_COUNT = 3;

// One past comment in the discussion history: the commenter's photo beside the
// space name and comment text, above the card's bottom border.
function AnswerRowSkeleton() {
    const s = useStyles();
    return (
        <View style={s.answerCard}>
            <View style={s.answerRow}>
                <BaseSkeleton borderRadius={20} height={40} marginRight={12} width={40} />
                <View style={s.answerText}>
                    <TextContainerSkeleton borderRadius={2} height={6} width={260} />
                    <Pad size={8} />
                    <View style={s.answerBody}>
                        <TextContainerSkeleton borderRadius={2} height={6} />
                    </View>
                </View>
            </View>
        </View>
    );
}

// Mirrors `ViewProfileAnswers`: the section heading, the answer rows, and for
// the owner, the visibility tag and visibility-edit button.
function DiscussionHistorySkeleton({ isOwner }: { isOwner: boolean }) {
    return (
        <PadBox vert={32}>
            <TextContainerSkeleton borderRadius={2} height={10} width={120} />
            {isOwner && (
                <>
                    <Pad size={16} />
                    <TextContainerSkeleton borderRadius={2} height={6} width={160} />
                </>
            )}
            <Pad size={24} />
            {Array.from({ length: ANSWER_ROW_COUNT }).map((_, i) => (
                // eslint-disable-next-line react/no-array-index-key
                <View key={i}>
                    {i > 0 && <Pad size={16} />}
                    <AnswerRowSkeleton />
                </View>
            ))}
            {isOwner && (
                <>
                    <Pad size={24} />
                    <ButtonSkeleton width={131} />
                </>
            )}
        </PadBox>
    );
}

export type ProfileBodySkeletonProps = { isOwner?: boolean };

// Mirrors `Profile`: the photo-and-name module, then discussion history, split
// by a separator. `isOwner` adds the controls only the profile's owner sees.
export function ProfileBodySkeleton({ isOwner = false }: ProfileBodySkeletonProps) {
    return (
        <PadBox bottom={40} horiz={20}>
            <PadBox vert={32}>
                <AvatarHeadingSkeleton />
                {isOwner && (
                    <>
                        <Pad size={20} />
                        <ButtonSkeleton width={189} />
                    </>
                )}
            </PadBox>
            <Separator />
            <DiscussionHistorySkeleton isOwner={isOwner} />
        </PadBox>
    );
}

export function SkeletonStandaloneProfileScreen() {
    const isDesktop = useIsDesktopBreakpoint();
    const s = useStyles();
    return (
        <View style={s.outer}>
            <LoadingAnnouncement />
            <NavBarSkeleton large={isDesktop} />
            <StandaloneLayout>
                <ProfileBodySkeleton />
            </StandaloneLayout>
        </View>
    );
}

export function SkeletonSidebarProfileScreen() {
    const s = useStyles();
    return (
        <View style={s.outer}>
            <LoadingAnnouncement />
            <SidebarNavBarSkeleton />
            <ConversationScreen>
                <ProfileBodySkeleton />
            </ConversationScreen>
        </View>
    );
}

const useStyles = createThemedStyles((tokens) => ({
    outer: { flex: 1, backgroundColor: tokens.colors.colorSurfacePrimary },
    answerCard: { paddingBottom: 16, borderBottomWidth: 1, borderBottomColor: tokens.colors.colorBorderPrimary },
    answerRow: { flexDirection: 'row', alignItems: 'center' },
    answerText: { flex: 1 },
    answerBody: { maxWidth: 400 },
}));
